import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  type HeatmapResponse,
  analyticsApi,
} from "../api/analytics";
import { humanizeApiError } from "../api/client";
import {
  DOCUMENT_STATUS_LABELS,
  type Document,
  documentsApi,
  humanizeStatusError,
} from "../api/documents";
import { useAuth } from "../auth/useAuth";
import { HeatmapPagesView } from "../components/HeatmapPagesView";
import { Breadcrumbs } from "../components/ui/breadcrumbs";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ConfirmDialog } from "../components/ui/confirm-dialog";
import { Input } from "../components/ui/input";

function formatDate(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toLocaleString("ru-RU", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function extractMessage(err: unknown): string {
  return humanizeApiError(err, { entityLabel: "Документ" });
}

function BackToDocuments() {
  const navigate = useNavigate();
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => navigate("/documents")}
      data-testid="back-to-documents"
    >
      <span aria-hidden="true" className="mr-1">←</span>К документам
    </Button>
  );
}

export function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [document, setDocument] = useState<Document | null>(null);
  const [heatmap, setHeatmap] = useState<HeatmapResponse | null>(null);
  const [uniqueSessions, setUniqueSessions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replacing, setReplacing] = useState(false);
  const [replaceMessage, setReplaceMessage] = useState<string | null>(null);
  const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false);
  const replaceInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [doc, h] = await Promise.all([
          documentsApi.get(id!),
          analyticsApi.documentHeatmap(id!, {
            layer: "both",
            unique_sessions: uniqueSessions,
          }),
        ]);
        if (cancelled) return;
        setDocument(doc);
        setHeatmap(h);
      } catch (err) {
        if (!cancelled) setError(extractMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, uniqueSessions]);

  async function onReplacePdf(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setReplacing(true);
    setReplaceMessage(null);
    setError(null);
    try {
      const updated = await documentsApi.replacePdf(id, file);
      setDocument(updated);
      // Перечитываем heatmap: количество страниц могло измениться.
      const h = await analyticsApi.documentHeatmap(id, {
        layer: "both",
        unique_sessions: uniqueSessions,
      });
      setHeatmap(h);
      setReplaceMessage("PDF заменён");
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setReplacing(false);
      if (replaceInputRef.current) replaceInputRef.current.value = "";
    }
  }

  if (!id) {
    return <div className="text-muted-foreground">Не указан id документа.</div>;
  }

  if (error && document === null) {
    return (
      <div className="space-y-4">
        <BackToDocuments />
        <Breadcrumbs
          items={[
            { label: "Документы", to: "/documents" },
            { label: "Документ" },
          ]}
        />
        <Card>
          <CardContent className="text-red-700 py-3 text-sm">{error}</CardContent>
        </Card>
      </div>
    );
  }

  if (loading && document === null) {
    return <div className="text-muted-foreground">Загрузка…</div>;
  }

  if (document === null || heatmap === null) {
    return null;
  }

  const replaceAllowed =
    isAdmin &&
    (document.status === "ready" || document.status === "conversion_error");

  return (
    <div className="space-y-4">
      <BackToDocuments />
      <Breadcrumbs
        items={[
          { label: "Документы", to: "/documents" },
          { label: document.title },
        ]}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">
            {document.title}
            {document.is_template && (
              <span className="ml-2 inline-block rounded px-2 py-0.5 text-xs bg-blue-50 text-blue-700">
                Шаблон
              </span>
            )}
            {document.needs_manual_review && (
              <span
                data-testid="needs-review-badge"
                title="После конвертации число страниц не совпало с оригиналом. Сверьте страницы со страницами оригинала — при необходимости перезалейте файл."
                className="ml-2 inline-block rounded px-2 py-0.5 text-xs text-amber-800 bg-amber-100"
              >
                Проверьте страницы
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Файл</dt>
            <dd>{document.original_filename}</dd>
            <dt className="text-muted-foreground">Формат</dt>
            <dd className="uppercase">{document.original_format}</dd>
            <dt className="text-muted-foreground">Страниц</dt>
            <dd>{document.pages_count ?? "—"}</dd>
            <dt className="text-muted-foreground">Загружен</dt>
            <dd>{formatDate(document.created_at)}</dd>
            <dt className="text-muted-foreground">Статус</dt>
            <dd>
              {DOCUMENT_STATUS_LABELS[document.status]}
              {document.status === "conversion_error" && document.status_error && (
                <div
                  className="text-xs text-red-700 mt-1 max-w-md"
                  title={document.status_error}
                >
                  {humanizeStatusError(document.status_error)}
                </div>
              )}
            </dd>
          </dl>

          {isAdmin && (
            <div className="mt-4 pt-4 border-t">
              <div className="text-sm font-medium mb-2">Заменить PDF</div>
              <p className="text-xs text-muted-foreground mb-2">
                Загрузите исправленный PDF — все ранее созданные ссылки начнут
                показывать новый контент. Лимит 50 МБ / 100 страниц.
              </p>
              <Input
                ref={replaceInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={onReplacePdf}
                disabled={replacing || !replaceAllowed}
                className="hidden"
                data-testid="replace-pdf-input"
              />
              <div className="flex items-center gap-3">
                <Button
                  disabled={replacing || !replaceAllowed}
                  onClick={() => setReplaceConfirmOpen(true)}
                  data-testid="replace-pdf-button"
                >
                  {replacing ? "Загрузка…" : "Заменить PDF"}
                </Button>
                {!replaceAllowed && (
                  <span className="text-xs text-muted-foreground">
                    Доступно после завершения конвертации
                  </span>
                )}
                {replaceMessage && (
                  <span
                    className="text-xs text-emerald-700"
                    data-testid="replace-pdf-success"
                  >
                    {replaceMessage}
                  </span>
                )}
              </div>
              {error && document !== null && (
                <div
                  className="text-xs text-red-700 mt-2"
                  data-testid="replace-pdf-error"
                >
                  {error}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">
            Тепловая карта (по документу)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <HeatmapPagesView
            heatmap={heatmap}
            pagesCount={document.pages_count ?? 0}
            uniqueSessions={uniqueSessions}
            onUniqueSessionsChange={setUniqueSessions}
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={replaceConfirmOpen}
        title="Заменить PDF документа?"
        description={
          <>
            Все активные ссылки начнут показывать новое содержимое.
            Старая версия не будет восстановлена.
          </>
        }
        confirmLabel="Выбрать новый PDF"
        busy={replacing}
        onConfirm={() => {
          setReplaceConfirmOpen(false);
          replaceInputRef.current?.click();
        }}
        onCancel={() => setReplaceConfirmOpen(false)}
      />
    </div>
  );
}
